import React, { useState, useEffect } from "react";
import CourseForm from "../../../../Components/Form/CourseForm"; // Use CourseForm instead of UserForm
import { useNavigate } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../../../../toastmsg";
import { courseListingFields } from "../../../../formSchemas/courseFormSchemas";
import { db, auth } from "../../../../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { slugify } from "../../../../utils/slug";

export default function AddCourse() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courseFields, setCourseFields] = useState(courseListingFields);

  // Fetch university names from Firebase
  useEffect(() => {
    const fetchUniversityNames = async () => {
      try {
        const querySnapshot = await getDocs(
          collection(db, "university-listing")
        );
        const names = querySnapshot.docs
          .map((doc) => doc.data().universityName)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        // Update course fields with dynamic university options
        const updatedFields = courseListingFields.map((field) =>
          field.name === "universityName" ? { ...field, options: names } : field
        );
        setCourseFields(updatedFields);
      } catch (error) {
        console.error("Error fetching university names:", error);
      }
    };
    fetchUniversityNames();
  }, []);

  const handleAddCourse = async (formData: any) => {
    setLoading(true);
    try {
      // ✅ Only include valid form fields defined in your schema
      const allowedKeys = courseListingFields.map((f) => f.name);

      const filteredData = Object.keys(formData)
        .filter((key) => allowedKeys.includes(key))
        .reduce((obj: any, key) => {
          obj[key] = formData[key];
          return obj;
        }, {});

      const newCourse = {
        ...filteredData,
        tuitionFee: Number(formData.tuitionFee),
        registrationFee: Number(formData.registrationFee),
        resourceFee: Number(formData.resourceFee),
        extraFees: Array.isArray(formData.extraFees) ? formData.extraFees : [],
        entryDetails: Array.isArray(formData.entryDetails)
          ? formData.entryDetails
          : [],
        englishRequirements: Array.isArray(formData.englishRequirements)
          ? formData.englishRequirements
          : [],
        courseSlug: slugify(
          `${formData.universityName} ${formData.courseName}`
        ),
        uniSlug: slugify(`${formData.universityName}`),
        majorSlug: slugify(`${formData.major}`),
        fieldOfStudySlug: slugify(`${formData.fieldOfStudy}`),
      };

      // Check if soft-deleted course exists
      const q = query(
        collection(db, "course-listing"),
        where("courseName", "==", newCourse.courseName)
      );
      const snapshot = await getDocs(q);

      let revived = false;

      if (!snapshot.empty) {
        const match = snapshot.docs.find(
          (docSnap) => docSnap.data().deleted === true
        );
        if (match) {
          await updateDoc(doc(db, "course-listing", match.id), {
            ...newCourse,
            deleted: false,
            updatedAt: new Date().toISOString(),
          });
          revived = true;
        }
      }

      if (!revived) {
        const docRef = await addDoc(
          collection(db, "course-listing"),
          newCourse
        );
        await updateDoc(doc(db, "course-listing", docRef.id), {
          ID: docRef.id,
        });
      }

      navigate("/course-listing");
      showSuccessToast(
        revived ? "Soft-deleted course revived." : "Course added successfully"
      );
    } catch (error: any) {
      console.error("Error adding course:", error);
      showErrorToast(error.message || "Failed to add course");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate("/course-listing");

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Add New Course</h2>
      <CourseForm
        initialData={{}}
        onSubmit={handleAddCourse}
        onCancel={handleCancel}
        disabled={loading}
      />
    </div>
  );
}
